import { sendStepUserJourney } from "@/api/userJourney";
import usePageSubmit from "@/hooks/usePageSubmit";
import useTabsValidation from "@/hooks/useTabsValidation";
import { useTagManager } from "@/hooks/useTagManager";
import { useConfigStore } from "@/store/config";
import { useFormStore } from "@/store/form";
import { useProductStore } from "@/store/product";
import { getOnlyNumbers } from "@/utils";
import { formatPrice } from "@/utils/currency";
import {
  getUuidStorageSession,
  setUuidStorageSession,
} from "@/utils/saleSessionStorage";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { GenericObject } from "@/utils/types";
import { useEffect, useState } from "react";
import { v4 as uuidV4 } from "uuid";

type PageConfig = {
  route: string;
  prevRoute: string;
  nextRoute: string;
};

const defaultPageConfig = {
  route: "/type-product",
  prevRoute: "/personal-data",
  nextRoute: "/address-info",
};

export function useTypeProduct() {
  const { dataLayer } = useTagManager();
  const form = useFormStore((store) => store.value);
  const shortFlowOn = useConfigStore((store) => store.shortFlowOn);
  const variantSpeedFlow = useConfigStore((store) => store.variantSpeedFlow);

  const isSpeedFlow =
    form.tipo === "MIGRACAO" &&
    shortFlowOn &&
    variantSpeedFlow.includes(form.utm_medium);
  useEffect(() => {
    if (isSpeedFlow) {
      setPageConfig({
        ...defaultPageConfig,
        nextRoute: "/short-flow-finish-order",
      });
    } else {
      setPageConfig(defaultPageConfig);
    }
  }, [isSpeedFlow]);

  const addData = useFormStore((store) => store.addData);

  const addBonusPortability = useProductStore(
    (store) => store.addBonusPortability
  );
  const removeBonusPortability = useProductStore(
    (store) => store.removeBonusPortability
  );
  const product = useProductStore((store) => store.item);

  const shouldAddSpecialBonus = useProductStore(
    (store) => store.shouldAddSpecialBonus
  );
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());

  const [pageConfig, setPageConfig] = useState<PageConfig>(defaultPageConfig);

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);

  const [typeProductSelected, setTypeProductSelected] = useState(
    form.tipo || "MIGRACAO"
  );

  const handlerGBProduct = () => {
    if (form.tipo === "PORTABILIDADE" || shouldAddSpecialBonus()) {
      addBonusPortability();
    } else {
      removeBonusPortability();
    }
  };
  const submitCallback = usePageSubmit(pageConfig.nextRoute);

  const handleOnSubmit = (formData: GenericObject) => {
    dataLayer({
      event: "step",
      passo: "TIPO-DE-SOLICITACAO",
      sku: removeAccentsAndSetUpperCase(product?.tim_id),
      nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
        product?.nome
      )}-${totalGbProduct}`,
      valor: formatPrice(currentPrice),
      status: "Sucesso",
    });
    handlerGBProduct();
    submitCallback({ ...formData, tipo: typeProductSelected });
  };

  const onClickForwardButton = () => {
    const passo = "ESCOLHER_TIPO";

    sendStepUserJourney({
      passo,
      uuid: form.uuid,
    });
  };

  useEffect(() => {
    const uuidForm = form.uuid;
    if (uuidForm) {
      setUuidStorageSession(uuidForm);
    }

    if (!form.uuid) {
      const uuidStorageSession = getUuidStorageSession();
      if (uuidStorageSession) {
        addData({ uuid: JSON.parse(uuidStorageSession) });
      } else {
        const uuid = uuidV4();
        if (uuid) {
          setUuidStorageSession(uuid);
        }
        addData({ uuid });
      }
    }
  }, [addData, form]);

  return {
    typeProductSelected,
    pageConfig,
    handleOnSubmit,
    setTypeProductSelected,
    onClickForwardButton,
    isSpeedFlow,
  };
}
